import React, { useState } from 'react'
import moment from 'moment'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  LinearProgress,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import FileField from '../../component/shared/component/FileFieldV2'
import { usePostImportPopulateCP58 } from '../../api/cp-58'
import NoteAddIcon from '@mui/icons-material/NoteAdd'

const SUPPORTED_FORMATS = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
}

const FILE_SIZE = 4 * 1000 * 1000 //4MB

// Yup runs all tests, hence need to check whether file exists before performing further validation
const validationSchema = yup.object({
  file: yup
    .mixed()
    .test('required', 'File is required', (file) => {
      return file instanceof File
    })
    .test('xlsx', 'Only .xlsx files are allowed', (file) => {
      if (!file) return false

      return file.name.toLowerCase().endsWith('.xlsx')
    })
    .test('fileSize', 'File must be smaller than 4 MB', (file) => {
      if (!file) return false

      return file.size <= FILE_SIZE
    }),
})

const Populate = () => {
  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitted },
    register,
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const [open, setOpen] = useState(false)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = usePostImportPopulateCP58()

  const onClose = () => {
    // Reset form
    reset()

    // Close dialog
    setOpen(false)
  }

  const onSubmit = async (values) => {
    // Init form data
    const formData = new FormData()

    // Append file
    formData.append('file', values.file)

    try {
      // Post
      const res = await mutateAsync(formData)

      // Get timestamp
      const timestamp = moment().format('YYYYMMDDHHmmss')
      const url = URL.createObjectURL(res.data)

      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${timestamp}_Populated_${values.file.name}`)
      document.body.appendChild(link)
      link.click()
      onClose()
    } catch (e) {
      // Display error on popup
      enqueueSnackbar(e?.response?.data?.message ?? 'Failed to export data', {
        variant: 'error',
        autoHideDuration: 7000,
      })

      console.error(e?.response)
    }
  }

  return (
    <div>
      {/* Button */}
      <Button startIcon={<NoteAddIcon />} onClick={() => setOpen(true)}>
        Populate CP 58 Import
      </Button>
      {/* Dialog Form */}
      <Dialog open={open} onClose={onClose}>
        {/* Loader */}
        {isLoading && <LinearProgress color='success'></LinearProgress>}
        <DialogTitle>Populate CP 58 Import</DialogTitle>
        {/* Import Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant='h6'>
                  Please submit a .xlsx import file to populate empty fields
                </Typography>
                <Typography variant='caption'>
                  * Only address1, address2, registrationNo, taxNo, nationality columns will be
                  populated
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2'>
                  Example template used for populate,{' '}
                  <a
                    href={'/documents/CP58_Import_Template.xlsx'}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    click to download
                  </a>
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FileField
                  inputProps={{
                    ...register('file'),
                    onChange: (e) => {
                      setValue('file', e?.target?.files[0])

                      // Manually trigger field/yup validation if form submitted once
                      // This is to reset failed validation after submit
                      if (isSubmitted) {
                        trigger('file')
                      }
                    },
                    type: 'file',
                    accept: SUPPORTED_FORMATS,
                  }}
                  error={errors?.file}
                  helperText={errors?.file && errors?.file?.message}
                  label={'File Import'}
                  required={true}
                />
              </Grid>
            </Grid>
          </DialogContent>
          {/* Actions */}
          <DialogActions>
            <Button variant='outlined' onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
              Populate
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default Populate
